import { Collapse } from '@mui/material'
import { ApiClientTradeBuildingSettings, ApiNote } from 'api/rtk-api'
import { Alert, Card, CurrencyFieldV2, PresentationalTable } from 'components/core-v2'
import { difference } from 'lodash'
import { useEffect } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { formatCurrencyFull } from 'utilities/currency'
import { displayPercentage } from 'utilities/number'
import { unformatNumberString } from 'utilities/string'

import { CURRENCY } from '../../../../../const'
import classes from './trade-table.module.scss'

interface Props {
  note: ApiNote
}

const emptyClientSettings: ApiClientTradeBuildingSettings = {
  minimumAdditionalTradeSize: 0,
  minimumNoteSize: 0,
  minimumTopUpSize: 0,
  minimumWithdrawalSize: 0
}

const EQUAL_ROW_OPTIONS = [
  { width: '30%' },
  { width: '20%' },
  { width: '10%', align: 'right' },
  { width: '23%', align: 'right' },
  { width: '17%', align: 'right' }
]

const EqualTable = ({ note }: Props) => {
  const [typeValue, totalAmountValue] = useWatch({ name: ['type', 'totalAmount'] })

  const topUpAmount = totalAmountValue
    ? formatCurrencyFull(
        unformatNumberString(totalAmountValue) / note.associatedTrades.length,
        CURRENCY
      )
    : '–'

  const rows = note.associatedTrades.map(trade => {
    const classification = trade.capitalPercentage > trade.yieldPercentage ? 'Capital' : 'Yield'
    let updatedAmount = '–'
    if (totalAmountValue) {
      if (typeValue === 'top-up') {
        const number =
          unformatNumberString(totalAmountValue) / note.associatedTrades.length + trade.accountValue
        updatedAmount = formatCurrencyFull(number, CURRENCY)
      } else {
        const amount =
          trade.accountValue - unformatNumberString(totalAmountValue) / note.associatedTrades.length
        updatedAmount = amount < 0 ? 'N/A' : formatCurrencyFull(amount, CURRENCY)
      }
    }

    return [
      trade.accountName,
      classification,
      topUpAmount,
      formatCurrencyFull(trade.accountValue, CURRENCY),
      updatedAmount
    ]
  })

  return (
    <PresentationalTable
      headerRow={[
        'Name',
        'Classification',
        typeValue === 'top-up' ? 'Top up' : 'Withdraw',
        'Current value',
        'Updated value'
      ]}
      rowOptions={EQUAL_ROW_OPTIONS}
      rows={rows}
    />
  )
}

const PRORATED_ROW_OPTIONS = [
  { width: '25%' },
  { width: '15%' },
  { width: '10%', align: 'right' },
  { width: '10%', align: 'right' },
  { width: '23%', align: 'right' },
  { width: '17%', align: 'right' }
]

const ProratedTable = ({ note }: Props) => {
  const [typeValue, totalAmountValue] = useWatch({ name: ['type', 'totalAmount'] })
  const totalNoteValue = note.associatedTrades.reduce((sum, trade) => sum + trade.accountValue, 0)

  const rows = note.associatedTrades.map(trade => {
    const classification = trade.capitalPercentage > trade.yieldPercentage ? 'Capital' : 'Yield'
    const weight = trade.accountValue / totalNoteValue
    const weightedTopUpAmount = formatCurrencyFull(
      unformatNumberString(totalAmountValue) * weight,
      CURRENCY
    )
    let updatedAmount = '–'
    if (totalAmountValue) {
      if (typeValue === 'top-up') {
        updatedAmount = formatCurrencyFull(
          unformatNumberString(totalAmountValue) * weight + trade.accountValue,
          CURRENCY
        )
      } else {
        const amount = trade.accountValue - unformatNumberString(totalAmountValue) * weight
        updatedAmount = amount < 0 ? 'N/A' : formatCurrencyFull(amount, CURRENCY)
      }
    }

    return [
      trade.accountName,
      classification,
      weightedTopUpAmount,
      displayPercentage(weight, 2),
      formatCurrencyFull(trade.accountValue, CURRENCY),
      updatedAmount
    ]
  })

  return (
    <PresentationalTable
      headerRow={[
        'Name',
        'Classification',
        typeValue === 'top-up' ? 'Top up' : 'Withdraw',
        'Weight',
        'Current value',
        'Updated value'
      ]}
      rowOptions={PRORATED_ROW_OPTIONS}
      rows={rows}
    />
  )
}

const CUSTOM_ROW_OPTIONS = [
  { width: '25%' },
  { width: '15%' },
  { width: '20%', align: 'right' },
  { width: '23%', align: 'right' },
  { width: '17%', align: 'right' }
]
interface CustomProps extends Props {
  clientSettings: ApiClientTradeBuildingSettings
  setShowOver95PercentWarning: (value: boolean) => void
  totalNoteValue: number
}
const CustomTable = ({
  clientSettings,
  note,
  setShowOver95PercentWarning,
  totalNoteValue
}: CustomProps) => {
  const typeValue = useWatch({ name: 'type' })
  const tradeIds = note.associatedTrades.map(trade => trade.accountId)
  const tradeTopUpValues = useWatch({ name: tradeIds })
  const { clearErrors, setError } = useFormContext()

  const minimumTopUpNumber = clientSettings?.minimumTopUpSize
  const minimumNoteSizeNumber = clientSettings?.minimumNoteSize
  const minimumWithdrawalNumber = clientSettings?.minimumWithdrawalSize
  const minimumTopUpValue = formatCurrencyFull(minimumTopUpNumber, CURRENCY)
  const minimumNoteSizeValue = formatCurrencyFull(minimumNoteSizeNumber, CURRENCY)
  const minimumWithdrawalValue = formatCurrencyFull(minimumWithdrawalNumber, CURRENCY)
  useEffect(() => {
    const minimumTopUpError = {
      type: 'minTopup',
      message: `All requested top ups must be above the minimum top up amount of ${minimumTopUpValue}`
    }

    const minimumWithdrawalError = {
      type: 'minWithdrawal',
      message: `All requested withdrawals must be above the minimum withdrawal amount of ${minimumWithdrawalValue}`
    }
    const minimumNoteValueError = {
      type: 'minNoteValue',
      message: `The note cannot fall below the minimum note size of ${minimumNoteSizeValue}`
    }
    const withdrawalTooHighError = {
      type: 'withdrawalTooHigh',
      message: `The withdrawal amount for one or more trades is greater than it's current value. Please enter an amount lesser than the current value`
    }

    let hasSetError = false
    let hasSetWarning = false

    const totalAmount = tradeTopUpValues.reduce(
      (sum, value) => sum + unformatNumberString(value),
      0
    )
    // Minimum note size
    if (typeValue === 'withdraw' && totalNoteValue - totalAmount < minimumNoteSizeNumber) {
      setError('group', minimumNoteValueError)
      hasSetError = true
    }

    tradeTopUpValues.forEach((fieldValue, index) => {
      const trade = note.associatedTrades[index]
      const value = unformatNumberString(fieldValue)
      if (typeValue === 'top-up') {
        if (value !== 0 && value < minimumTopUpNumber) {
          // Minimum top-up
          setError('group', minimumTopUpError)
          hasSetError = true
        }
      }
      if (typeValue === 'withdraw') {
        // Minimum withdrawal
        if (value !== 0 && value < minimumWithdrawalNumber) {
          setError('group', minimumWithdrawalError)
          hasSetError = true
        }
        // Withdrawal over 95% the value of a single trade
        if (value / trade.accountValue > 0.95) {
          setShowOver95PercentWarning(true)
          hasSetWarning = true
        }
        // Withdrawal more than a given trade
        if (value > trade.accountValue) {
          setError('group', withdrawalTooHighError)
          hasSetError = true
        }
      }
    })

    if (!hasSetError) {
      clearErrors('group')
    }
    if (!hasSetWarning) {
      setShowOver95PercentWarning(false)
    }
  }, [
    clearErrors,
    minimumNoteSizeNumber,
    minimumNoteSizeValue,
    minimumTopUpNumber,
    minimumTopUpValue,
    minimumWithdrawalNumber,
    minimumWithdrawalValue,
    note.associatedTrades,
    setError,
    setShowOver95PercentWarning,
    totalNoteValue,
    tradeTopUpValues,
    typeValue
  ])
  const rows = note.associatedTrades.map(trade => {
    const classification = trade.capitalPercentage > trade.yieldPercentage ? 'Capital' : 'Yield'
    const topUpValue = tradeTopUpValues[tradeIds.findIndex(id => id === trade.accountId)]
    let updatedAmount = '-'
    if (topUpValue) {
      if (typeValue === 'top-up') {
        updatedAmount = formatCurrencyFull(
          trade.accountValue + unformatNumberString(topUpValue),
          CURRENCY
        )
      } else {
        const amount = trade.accountValue - unformatNumberString(topUpValue)
        updatedAmount = amount < 0 ? 'N/A' : formatCurrencyFull(amount, CURRENCY)
      }
    }
    return [
      trade.accountName,
      classification,
      <CurrencyFieldV2 key={trade.accountId} showError id={trade.accountId} label="" />,
      formatCurrencyFull(trade.accountValue, CURRENCY),
      updatedAmount
    ]
  })

  return (
    <PresentationalTable
      headerRow={[
        'Name',
        'Classification',
        typeValue === 'top-up' ? 'Top up' : 'Withdraw',
        'Current value',
        'Updated value'
      ]}
      rowOptions={CUSTOM_ROW_OPTIONS}
      rows={rows}
    />
  )
}

const errorTitles = {
  minTopup: 'Minimum top up amount not reached',
  minWithdrawal: 'Minimum withdrawal amount not reached',
  minNoteValue: 'Note value too low',
  over95PercentWithdrawal: 'Warning, you are about to withdraw 95% of all funds',
  withdrawalTooHigh: 'Withdrawal amount too high'
}
const nonCustomTradeFields = ['type', 'amountType', 'confirmation', 'totalAmount']

interface TradeTableProps extends Props {
  clientSettings: ApiClientTradeBuildingSettings
  setShowOver95PercentWarning: (value: boolean) => void
  showOver95PercentWarning: boolean
}
export const TradeTable = ({
  clientSettings = emptyClientSettings,
  note,
  setShowOver95PercentWarning,
  showOver95PercentWarning
}: TradeTableProps) => {
  const [amountTypeValue, totalAmountValue, typeValue] = useWatch({
    name: ['amountType', 'totalAmount', 'type']
  })

  const tradeIds = note.associatedTrades.map(trade => trade.accountId)
  const tradeTopUpValues = useWatch({ name: tradeIds })
  const { formState } = useFormContext()
  const errors = Object.entries(formState.errors).filter(([key]) => key !== 'confirmation')
  const showError =
    amountTypeValue === 'custom'
      ? difference(Object.keys(formState.touchedFields), nonCustomTradeFields).length > 0 &&
        errors.length > 0
      : formState.touchedFields.totalAmount && errors.length > 0

  const totalNoteValue = note.associatedTrades.reduce((sum, trade) => sum + trade.accountValue, 0)

  const isEqual = amountTypeValue === 'equal'
  const isProrated = amountTypeValue === 'prorated'
  const isCustom = amountTypeValue === 'custom'

  let totalAmount: string | number = '–'
  let totalUpdatedValue = '–'

  if ((isEqual || isProrated) && amountTypeValue) {
    totalAmount = formatCurrencyFull(unformatNumberString(totalAmountValue), CURRENCY)
    totalUpdatedValue =
      typeValue === 'top-up'
        ? formatCurrencyFull(
            unformatNumberString(totalAmountValue) + Number(totalNoteValue),
            CURRENCY
          )
        : formatCurrencyFull(
            Number(totalNoteValue) - unformatNumberString(totalAmountValue),
            CURRENCY
          )
  }
  if (isCustom) {
    const totalCustomTopUps = tradeTopUpValues.reduce(
      (sum, value) => sum + (value ? unformatNumberString(value) : 0),
      0
    )
    totalAmount = formatCurrencyFull(totalCustomTopUps, CURRENCY)
    if (totalCustomTopUps) {
      totalUpdatedValue =
        typeValue === 'top-up'
          ? formatCurrencyFull(totalNoteValue + totalCustomTopUps, CURRENCY)
          : formatCurrencyFull(totalNoteValue - totalCustomTopUps, CURRENCY)
    }
  }

  const totalTableRows = isProrated
    ? [
        'Total',
        null,
        totalAmount,
        '100%',
        formatCurrencyFull(totalNoteValue, CURRENCY),
        totalUpdatedValue
      ]
    : ['Total', null, totalAmount, formatCurrencyFull(totalNoteValue, CURRENCY), totalUpdatedValue]

  const errorIndex = errors?.[0]?.[1]?.type as string

  return (
    <>
      <Card.Wrapper spacing="xs">
        {isEqual && <EqualTable note={note} />}
        {isProrated && <ProratedTable note={note} />}
        {isCustom && (
          <CustomTable
            clientSettings={clientSettings}
            note={note}
            setShowOver95PercentWarning={setShowOver95PercentWarning}
            totalNoteValue={totalNoteValue}
          />
        )}
      </Card.Wrapper>
      <Collapse in={showError}>
        <Alert className={classes.errorAlert} title={errorTitles[errorIndex]} type="error">
          {errors.map(([fieldKey, error]) => (
            <p key={fieldKey}>{String(error.message)}</p>
          ))}
        </Alert>
      </Collapse>
      <Collapse in={showOver95PercentWarning}>
        <Alert
          className={classes.errorAlert}
          title={errorTitles.over95PercentWithdrawal}
          type="warning"
        >
          Withdrawing over 95% of all funds will result in the trade closing
        </Alert>
      </Collapse>
      <Card.Wrapper className="mt-4 mb-10" spacing="xs">
        <PresentationalTable
          rowOptions={isProrated ? PRORATED_ROW_OPTIONS : EQUAL_ROW_OPTIONS}
          rows={[totalTableRows]}
        />
      </Card.Wrapper>
    </>
  )
}
